"use client";

import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";
import { AppendReturnUrl } from "@/services/AppendReturnUrl";
import { useSearchParams } from "next/navigation";
import { useEffect } from "react";

interface AuthError {
  title: string;
  body: string;
}

const AUTH_ERROR_MESSAGES: {
  [key: string]: AuthError;
} = {
  Verification: {
    title: "Login failed, please try again",
    body: "The link you used is invalid or has expired.  Please contact us if the problem persists.",
  },
  AccessDenied: {
    title: "Access denied",
    body: "You do not have permission to access this account.",
  },
  Configuration: {
    title: "Sorry, something went wrong",
    body: "We'll be taking a look, please contact us if the problem persists.",
  },
};

const AUTH_ERROR_DEFAULT: AuthError = {
  title: "Please try again or contact us for support",
  body: "An unknown error occurred.",
};

export function useAuthSearchParams() {
  const searchParams = useSearchParams();
  let returnUrl = searchParams.get("returnUrl");
  if (!returnUrl || returnUrl.startsWith("/auth")) {
    returnUrl = "/";
  }
  const emailOrUsernameParam = searchParams.get("emailOrUsername") ?? "";
  const errorKey = searchParams.get("error");
  const error = errorKey
    ? (AUTH_ERROR_MESSAGES[errorKey] ?? AUTH_ERROR_DEFAULT)
    : undefined;

  useEffect(() => {
    CreateAnalyticEvent({
      event: {
        adIds: ["NONE"],
        eventType: "AUTH#ERROR",
        metadata: {
          error: errorKey,
        },
      },
    });
  }, [error]);

  return {
    returnUrl,
    emailOrUsernameParam,
    appendAuthSearchParams: (url: string, newEmailOrUsername?: string) =>
      AppendReturnUrl(url, returnUrl) +
      ((newEmailOrUsername ?? emailOrUsernameParam)
        ? `&emailOrUsername=${encodeURIComponent(newEmailOrUsername ?? emailOrUsernameParam)}`
        : ""),
    error: error,
  };
}
